import classNames from '@/common/utils/classNames';
import type { LucideIcon } from 'lucide-react';
import {
  ButtonHTMLAttributes,
  CSSProperties,
  ChangeEvent,
  KeyboardEvent,
  InputHTMLAttributes,
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

/**
 * Telegram-style settings building blocks (owner's references, 2026-10-14: Telegram Android
 * Account / Privacy and Security / Chat Settings). Use these for every settings-like screen so
 * the app looks the same everywhere:
 * - page background `SETTINGS_PAGE_BG`, white rounded sections with an accent header and an
 *   optional muted footer under the card
 * - rows: icon (colored circle, or a plain gray glyph), title + muted subtitle, value at the end in
 *   the accent color; dividers start at the text, never under the icon; no chevrons
 * - inputs are borderless rows inside a section (like «Your name»)
 */

export const SETTINGS_PAGE_BG = 'bg-[#F2F2F2]';

/** Main menus (Telegram's Settings tab) have no dividers; detail screens do. */
const DividersContext = createContext(true);

export const SettingsSection = ({
  title,
  footer,
  dividers = true,
  children,
  className,
}: {
  title?: ReactNode;
  footer?: ReactNode;
  dividers?: boolean;
  children: ReactNode;
  className?: string;
}) => (
  <section className={className}>
    <DividersContext.Provider value={dividers}>
      <div className="overflow-hidden rounded-[14px] bg-white">
        {title && <h3 className="px-[18px] pb-1 pt-4 text-[13px] font-semibold leading-5 text-primary">{title}</h3>}
        <div className={classNames('flex flex-col', !title && 'pt-0', 'pb-1')}>{children}</div>
      </div>
    </DividersContext.Provider>
    {footer && <p className="px-4 pb-1 pt-1.5 text-[12px] leading-[18px] text-[#8a8a8a]">{footer}</p>}
  </section>
);

/** Inset divider under every row but the last one in its section. */
const DIVIDER =
  "relative after:absolute after:bottom-0 after:end-0 after:start-[var(--divider-start)] after:h-px after:bg-black/[0.08] last:after:hidden after:content-['']";
/** Where the divider starts: under the text, never under the icon (padding + icon + gap). */
const dividerStart = (icon: boolean, colored: boolean) => ({
  '--divider-start': icon ? (colored ? '65px' : '53px') : '16px',
});

const RowIcon = ({ icon: Icon, color, menu }: { icon: LucideIcon; color?: string; menu: boolean }) =>
  color && (menu || color.includes('bg-slate-100')) ? (
    <span
      className={classNames(
        'relative flex h-[28px] w-[28px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] text-white after:pointer-events-none after:absolute after:inset-0 after:bg-gradient-to-b after:from-white/20 after:to-black/[0.08] after:content-[""]',
        color,
      )}
    >
      <Icon size={20} strokeWidth={2.1} className="relative z-[1]" aria-hidden />
    </span>
  ) : (
    <Icon size={21} strokeWidth={1.8} className="shrink-0 text-[#6d7883]" aria-hidden />
  );

export const SettingsRow = ({
  icon,
  iconColor,
  title,
  subtitle,
  value,
  end,
  tone = 'default',
  onClick,
}: {
  icon?: LucideIcon;
  /** Tailwind bg class → colored circle; omitted → plain gray glyph (Telegram's security rows). */
  iconColor?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Short value at the end, in the accent color («فعال», a count…). */
  value?: ReactNode;
  /** Any trailing control (switch, badge) instead of `value`. */
  end?: ReactNode;
  tone?: 'default' | 'accent' | 'danger';
  onClick?: () => void;
}) => {
  // A row with its own control at the end (a switch) can't be a <button>; it stays tappable as a div.
  const Tag = onClick && !end ? 'button' : 'div';
  const dividers = useContext(DividersContext);
  return (
    <Tag
      {...(onClick
        ? end
          ? {
              role: 'button',
              tabIndex: 0,
              onClick,
              onKeyDown: (event: KeyboardEvent) => (event.key === 'Enter' || event.key === ' ') && onClick(),
            }
          : { type: 'button' as const, onClick }
        : {})}
      style={dividerStart(!!icon, !!iconColor && !dividers) as CSSProperties}
      className={classNames(
        'flex w-full items-center gap-[19px] ps-[18px] text-start',
        onClick && 'transition-colors duration-100 active:bg-black/[0.06]',
        dividers && DIVIDER,
      )}
    >
      {icon && <RowIcon icon={icon} color={iconColor} menu={!dividers} />}
      <span
        className={classNames(
          'flex min-w-0 flex-1 items-center gap-3 self-stretch pe-4',
          subtitle ? 'min-h-[60px] py-1.5' : 'min-h-[50px]',
        )}
      >
        <span className="min-w-0 flex-1">
          <span
            className={classNames(
              'line-clamp-2 text-[15px] leading-[23px]',
              tone === 'accent' ? 'text-primary' : tone === 'danger' ? 'text-red-500' : 'text-[#222]',
            )}
          >
            {title}
          </span>
          {subtitle && <span className="mt-1 block truncate text-[12.5px] leading-[19px] text-[#767c86]">{subtitle}</span>}
        </span>
        {value != null && <span className="shrink-0 text-[13px] text-primary">{value}</span>}
        {end}
      </span>
    </Tag>
  );
};

type SettingsInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  onValueChange?: (value: string) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Muted text at the end of the row (a counter, a unit). */
  end?: ReactNode;
  error?: boolean;
};

/** Borderless text field row (Telegram «Your name»). Put several in one SettingsSection. */
export const SettingsInput = forwardRef<HTMLInputElement, SettingsInputProps>(
  ({ onValueChange, onChange, end, error, className, ...rest }, ref) => {
    // A field that only takes digits (code, phone, national id) always reads left to right, in Latin digits.
    // The Latin font has no Persian glyphs, so an empty field (Persian placeholder) keeps the page font and direction.
    const numeric =
      (['numeric', 'tel', 'decimal'].includes(String(rest.inputMode)) || rest.type === 'tel' || rest.type === 'number') &&
      !(rest.value !== undefined && String(rest.value) === '');
    return (
      <label
        style={dividerStart(false, false) as CSSProperties}
        className={classNames('flex min-h-[48px] items-center gap-3 px-4', DIVIDER)}
      >
        <input
          ref={ref}
          {...(numeric ? { dir: 'ltr', style: { fontFamily: "Roboto, system-ui, 'Segoe UI', sans-serif" } } : {})}
          {...rest}
          onChange={event => {
            onChange?.(event);
            onValueChange?.(event.target.value);
          }}
          className={classNames(
            'min-w-0 flex-1 bg-transparent text-[15px] text-[#222] outline-none focus:outline-none focus-visible:outline-none placeholder:text-[#a3a3a3]',
            numeric && 'text-left',
            error && 'text-red-500',
            className,
          )}
        />
        {end && <span className="shrink-0 text-[13px] text-[#8a8a8a]">{end}</span>}
      </label>
    );
  },
);
SettingsInput.displayName = 'SettingsInput';

/** Full-width primary action pinned to the bottom of a settings screen. */
export const SettingsActionBar = ({ children }: { children: ReactNode }) => (
  <div className={classNames('sticky bottom-0 -mx-3 mt-auto px-3 pb-4 pt-3', SETTINGS_PAGE_BG)}>{children}</div>
);

/** Telegram's big action button (e.g. «Continue»); `loading` keeps its size and shows a spinner. */
export const SettingsButton = ({
  children,
  loading,
  variant = 'primary',
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean; variant?: 'primary' | 'plain' }) => (
  <button
    type="button"
    {...rest}
    disabled={rest.disabled || loading}
    className={classNames(
      'flex h-12 w-full items-center justify-center gap-2 rounded-[12px] text-[15px] font-medium transition-opacity disabled:opacity-50',
      variant === 'primary' ? 'bg-primary text-white active:opacity-85' : 'bg-white text-primary active:bg-black/[0.06]',
      className,
    )}
  >
    {loading ? (
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-label="در حال انجام" />
    ) : (
      children
    )}
  </button>
);

/** Telegram radio indicator for option rows (pass as SettingsRow `end`). */
export const SettingsRadio = ({ checked }: { checked: boolean }) => (
  <span
    aria-hidden
    className={classNames(
      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-150',
      checked ? 'border-primary' : 'border-[#b0b0b0]',
    )}
  >
    <span
      className={classNames('h-2.5 w-2.5 rounded-full bg-primary transition-transform duration-150', checked ? 'scale-100' : 'scale-0')}
    />
  </span>
);

/** Telegram accent action row («Change Chat Wallpaper»): accent glyph + accent title, may be disabled/loading. */
export const SettingsActionRow = ({
  icon: Icon,
  title,
  onClick,
  disabled,
  loading,
}: {
  icon: LucideIcon;
  title: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}) => {
  const dividers = useContext(DividersContext);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      style={dividerStart(true, false) as CSSProperties}
      className={classNames(
        'flex min-h-[46px] w-full items-center gap-4 px-4 text-start text-primary transition-colors duration-100 active:bg-black/[0.06] disabled:opacity-50',
        dividers && DIVIDER,
      )}
    >
      {loading ? (
        <span
          className="h-[21px] w-[21px] shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-label="در حال انجام"
        />
      ) : (
        <Icon size={21} strokeWidth={1.8} className="shrink-0" aria-hidden />
      )}
      <span className="min-w-0 flex-1 truncate text-[15px] leading-[23px]">{title}</span>
    </button>
  );
};

/**
 * Image (logo/avatar) for the SettingsRow icon slot; pass with `iconColor` (e.g. 'bg-slate-100')
 * so it gets the round icon frame: `<SettingsRow icon={settingsImageIcon(url)} iconColor="bg-slate-100" />`.
 * Cached per src so the row doesn't remount the image on every render; hides itself on load error.
 */
const imageIcons = new Map<string, LucideIcon>();
export const settingsImageIcon = (src: string): LucideIcon => {
  const cached = imageIcons.get(src);
  if (cached) return cached;
  const ImageIcon = () => (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt=""
      className="h-full w-full rounded-[10px] object-cover"
      onError={e => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
  ImageIcon.displayName = 'SettingsImageIcon';
  const icon = ImageIcon as unknown as LucideIcon;
  imageIcons.set(src, icon);
  return icon;
};

/** Latin text/numbers (URLs, paper sizes) in the Latin UI font: the page font draws ASCII digits as Persian ones. */
export const Latin = ({ children }: { children: ReactNode }) => (
  <span dir="ltr" style={{ display: 'inline-block', fontFamily: "Roboto, system-ui, 'Segoe UI', sans-serif" }}>
    {children}
  </span>
);

/**
 * Telegram/Eitaa edit screens save from the top bar (a check mark), never from a button pinned to the page bottom.
 * `SettingsBarActionHost` owns the bar; a screen declares its action with `<SettingsDone>`.
 */
type BarAction = { label: string; disabled?: boolean; loading?: boolean; onClick: () => void };
const BarActionContext = createContext<((action: BarAction | null) => void) | null>(null);

export const SettingsBarDoneButton = ({ action }: { action: BarAction }) => (
  <button
    type="button"
    aria-label={action.label}
    disabled={action.disabled || action.loading}
    onClick={action.onClick}
    className={classNames(
      'flex h-10 w-10 items-center justify-center rounded-full transition-colors active:bg-black/[0.06]',
      action.disabled ? 'text-[#b4b9c0]' : 'text-primary',
    )}
  >
    {action.loading ? (
      <span className="h-[22px] w-[22px] animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
    ) : (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="m5 12.5 4.5 4.5L19 7.5" />
      </svg>
    )}
  </button>
);

export const SettingsBarActionHost = ({
  renderBar,
  children,
}: {
  renderBar: (doneButton: ReactNode) => ReactNode;
  children: ReactNode;
}) => {
  const [action, setAction] = useState<BarAction | null>(null);
  return (
    <BarActionContext.Provider value={setAction}>
      {renderBar(action ? <SettingsBarDoneButton action={action} /> : undefined)}
      {children}
    </BarActionContext.Provider>
  );
};

export const SettingsDone = ({
  label,
  disabled,
  loading,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
}) => {
  const register = useContext(BarActionContext);
  const latest = useRef(onClick);
  latest.current = onClick;
  useEffect(() => {
    register?.({ label, disabled, loading, onClick: () => latest.current() });
    return () => register?.(null);
  }, [register, label, disabled, loading]);
  return null;
};
